'use client'
import { getLabel } from '@/entities/datePicker'
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/shadcn'

import { ru } from 'date-fns/locale'

import { CalendarIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { useState } from 'react'
import { useIsMobile } from '@/shared/utils'
import { cn } from '@/shared/lib/classnames'
import { getDefaultDateRange } from '../lib/get-dafault-date'

interface DatePickerWithRangeProps {
  className?: string
  variant?: 'default' | 'outline'
  size?: 'default' | 'lg'
  onDateChange?: (range: DateRange | undefined) => void
  defaultValue?: DateRange
  defaultPeriod?: 'today' | 'week' | 'month' | 'year'
}

export const DatePickerWithRange = ({
  className,
  size = 'default',
  variant = 'default',
  onDateChange,
  defaultValue,
  defaultPeriod,
}: DatePickerWithRangeProps) => {
  const isMobile = useIsMobile()

  const [date, setDate] = useState<DateRange | undefined>(
    getDefaultDateRange({ defaultValue, defaultPeriod })
  )

  const handleDateSelect = (selectedDate: DateRange | undefined) => {
    if (!selectedDate?.from) {
      // Пользователь сбросил всё — передаем undefined
      setDate(undefined)
      onDateChange?.(undefined)
      return
    }

    if (selectedDate.from && !selectedDate.to) {
      // Выбрана одна дата — считаем диапазон как один день
      const singleDayRange = {
        from: selectedDate.from,
        to: selectedDate.from,
      }
      setDate(singleDayRange)
      onDateChange?.(singleDayRange)
      return
    }

    // Обычное поведение, выбраны обе даты
    setDate(selectedDate)
    onDateChange?.(selectedDate)
  }

  return (
    <div className={'grid gap-2'}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            size={size}
            id="date"
            variant={variant}
            className={cn(!date && 'text-muted-foreground', className, 'text-xs md:text-base')}
          >
            <CalendarIcon className="!w-5 !h-5 text-primary dark:text-primary-dark" />
            <span>{getLabel(date, isMobile)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto" align="end">
          <Calendar
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleDateSelect}
            numberOfMonths={1}
            locale={ru}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
